import { defaultLocaleForDate } from '@/config/date'

/**
 * Reads the current UI language through `@/config/date` rather than `@/i18n`
 * directly. `formatDuration` is imported by nearly every info-card component
 * (many of them lazy-loaded), and a direct `@/i18n` import there created a
 * fresh entry point into that module from many separate async chunks —
 * that produced a circular-import TDZ error ("Cannot access 'default' before
 * initialization") breaking every card. `@/config/date` already re-exports
 * the same reactive locale and is already imported by these same components
 * (via `formatStartDate`/`formatDateTime`) without issue, so routing through
 * it here reuses a known-safe path instead of opening a new one.
 */
function currentUiLocale(): 'en' | 'ru' {
  return defaultLocaleForDate.value.startsWith('ru') ? 'ru' : 'en'
}

/** Compact unit suffixes per UI language: English `h`/`m`/`s`, Russian `ч`/`мин`/`с`. */
const UNIT_SUFFIXES = {
  en: { h: 'h', m: 'm', s: 's' },
  ru: { h: 'ч', m: 'мин', s: 'с' },
} as const

/**
 * Format an ISO 8601 duration string (e.g. `PT1H30M`, `PT45M`, `PT2H`) into a
 * compact, human-readable form like `1h 30m`, `45m`, or `2h` — `1ч 30мин`,
 * `45мин`, `2ч` when the UI language is Russian.
 *
 * Only the time-part designators `H`, `M`, `S` after `T` are recognized; the
 * date-part designators (`Y`, `Mo`, `W`, `D`) are intentionally not supported
 * because the backend only returns time-only durations for service types.
 */
export function formatDuration(raw: string | null | undefined): string | null {
  if (raw == null) return null
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/i.exec(trimmed)
  if (!match) return null
  const [, hStr, mStr, sStr] = match
  const suffixes = UNIT_SUFFIXES[currentUiLocale()]
  const parts: string[] = []
  if (hStr != null) {
    const h = Number(hStr)
    if (Number.isFinite(h) && h > 0) parts.push(`${h}${suffixes.h}`)
  }
  if (mStr != null) {
    const m = Number(mStr)
    if (Number.isFinite(m) && m > 0) parts.push(`${m}${suffixes.m}`)
  }
  if (sStr != null) {
    const s = Number(sStr)
    if (Number.isFinite(s) && s > 0) parts.push(`${s}${suffixes.s}`)
  }
  if (parts.length === 0) return null
  return parts.join(' ')
}
